import { useState } from 'react';
import { Drawer, Text, Pick, Err } from '../ui.js';
import { createRecord, updateRecord, sendEmail, ApiError } from '../api.js';
import { RichText, htmlToText, type RichValue } from './RichText.js';

// One composer, used from the account record, the contact record, and a
// sequence touch. Written once because "write an email to someone at this
// account" is the same job in all three places, and three copies would drift —
// the tracking, the plain-text half, and the suppression check would end up
// implemented two and a half times.

export interface Recipient {
  id: string; name: string; email: string | null; title?: string | null;
  suppressed?: boolean;
}

export interface ComposerTarget {
  companyId: string;
  /** Preselected recipient. Omit to make the operator choose. */
  contactId?: string | null;
  /** Everyone on the account, so the picker is a picker and not a text field. */
  recipients: Recipient[];
  /** Editing an existing draft or touch rather than starting fresh. */
  existing?: {
    id: string; subject: string | null; body: string | null;
    body_html?: string | null; status?: string;
    kind: 'email' | 'touch';
  } | null;
}

export function Composer({ target, onClose, onSaved }: {
  target: ComposerTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ex = target.existing ?? null;
  // An existing draft opens addressed to ITS OWN stored recipient, not the
  // caller's default — the picker showing person B over a draft stored for
  // person A was the first half of a misdelivery.
  const exContact = (ex as unknown as { contact_id?: string | null })?.contact_id;
  const [contactId, setContactId] = useState(exContact ?? target.contactId ?? '');
  const [subject, setSubject] = useState(ex?.subject ?? '');
  const [rich, setRich] = useState<RichValue>({
    html: ex?.body_html ?? (ex?.body ? ex.body.replace(/\n/g, '<br>') : ''),
    text: ex?.body ?? '',
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const to = target.recipients.find((r) => r.id === contactId) ?? null;
  const isTouch = ex?.kind === 'touch';
  const empty = !subject.trim() && !rich.text.trim();

  // A suppressed contact is set on a negative reply and it is permanent.
  // Blocking the send here as well as in the engine means the operator finds
  // out while writing, not after clicking.
  const blocked = to?.suppressed
    ? `${to.name} is suppressed — they asked not to be contacted.`
    : null;

  async function save(status: 'draft' | 'scheduled', thenSend = false) {
    setBusy(status); setError(null);
    try {
      const text = rich.text || htmlToText(rich.html);
      const payload = {
        subject: subject || null,
        body: text || null,
        body_html: rich.html || null,
      };

      if (isTouch) {
        await updateRecord('touches', ex!.id, payload);
        onSaved(); onClose();
        return;
      }

      let id = ex?.id;
      if (id) {
        // The recipient RIDES ALONG on every update. It used to be sent only
        // on create, so re-picking the To on an existing draft changed the
        // screen and not the row — Queue then mailed the OLD address while
        // the drawer displayed the new one. The second half of a misdelivery,
        // and the worse one.
        await updateRecord('emails', id, {
          ...payload, status,
          contact_id: contactId || null,
          to_email: to?.email ?? null, to_name: to?.name ?? null,
          thread_key: contactId ? `contact:${contactId}` : `company:${target.companyId}`,
        });
      } else {
        const r = await createRecord<{ id: string }>('emails', {
          ...payload,
          company_id: target.companyId,
          contact_id: contactId || null,
          direction: 'outbound', status,
          to_email: to?.email ?? null, to_name: to?.name ?? null,
          thread_key: contactId ? `contact:${contactId}` : `company:${target.companyId}`,
        });
        id = r.record.id;
      }

      if (thenSend && id) {
        const s = await sendEmail(id);
        if (s.failed) throw new ApiError(500, s.results?.[0]?.error ?? 'Send failed.');
        // sent:0, failed:0 is the engine deferring (cap reached, identity
        // unverified, suppressed) — the row quietly went back to scheduled.
        // Saying "sent" here would be a lie; say what actually happened.
        if (s.sent === 0) {
          throw new ApiError(200,
            'Not sent — the engine deferred it (a cap, the sending window, or a '
            + 'gate). It stays queued and goes with the next eligible drain.');
        }
      }
      onSaved(); onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  }

  return (
    <Drawer
      title={isTouch ? `Edit touch — ${ex?.subject || 'untitled'}`
        : ex ? 'Edit email' : 'New email'}
      subtitle={isTouch
        ? 'Saved with the sequence. It sends on its scheduled day.'
        : to?.email ?? 'Pick who this goes to'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="ghost" disabled={!!busy || empty}
            onClick={() => void save('draft')}>
            {busy === 'draft' ? 'Saving…' : isTouch ? 'Save touch' : 'Save draft'}
          </button>
          {!isTouch && (
            <>
              <button disabled={!!busy || empty || !to?.email || !!blocked}
                onClick={() => void save('scheduled')}>
                Queue
              </button>
              <button className="primary"
                disabled={!!busy || empty || !to?.email || !!blocked}
                onClick={() => void save('scheduled', true)}>
                {busy === 'scheduled' ? 'Sending…' : 'Send now'}
              </button>
            </>
          )}
        </>
      }
    >
      <Err>{error}</Err>
      {blocked && <p className="warnbox">{blocked}</p>}

      {!isTouch && (
        <Pick
          label="To"
          value={contactId}
          allowEmpty={false}
          hint={target.recipients.length
            ? 'Everyone on this account.'
            : 'No contacts on this account yet — add one first.'}
          options={target.recipients.map((r) => ({
            value: r.id,
            label: `${r.name}${r.title ? ` · ${r.title}` : ''}` +
              (r.email ? ` — ${r.email}` : ' — no address'),
          }))}
          onChange={setContactId}
        />
      )}

      {to && !to.email && (
        <p className="warnbox">
          {to.name} has no address yet. Enrich them and this can be queued.
        </p>
      )}

      <Text label="Subject" value={subject} autoFocus={!!contactId}
        onChange={(e) => setSubject(e.target.value)} />

      <RichText label="Body" value={rich} onChange={setRich} rows={14}
        hint="Pasted text arrives plain on purpose — Word markup renders badly in Outlook and reads as machine-generated." />
    </Drawer>
  );
}
